import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AppBar, Toolbar, Typography, Button, Container, Box, Menu, MenuItem, Divider } from '@mui/material';
import { FileText as ArticleIcon, ChevronDown as ArrowDownIcon, ClipboardList as AssignmentIcon } from 'lucide-react';
import LoginPage from './pages/Auth/LoginPage';
import SignupPage from './pages/Auth/SignupPage';
import OrganizationPage from './pages/Auth/OrganizationPage';
import PlansPage from './pages/Auth/PlansPage';
import PaymentPage from './pages/Auth/PaymentPage';
import SubscriptionSuccessPage from './pages/Auth/SubscriptionSuccessPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import Profile from './components/Profile';
import EmailVerification from './components/EmailVerification';
import DocumentsPage from './pages/Documents/DocumentsPage';
import ClaimListPage from './pages/Claims/ClaimListPage';
import ClaimFlowPage from './components/claims/ClaimFlowPage';
import TicketDetailPage from './pages/Claims/TicketDetail/TicketDetailPage';
import TrackPage from './pages/Claims/TrackPage';
import LineItemAppealPage from './pages/LineItemAppeal/LineItemAppealPage';
import PatientsPage from './pages/PatientsPage';
import ProvidersPage from './pages/ProvidersPage';
import LandingPage from './pages/LandingPage';
import AppealsPage from './pages/MinimalAppeal/AppealsPage';
import ApprovalsPage from './pages/Approvals/ApprovalsPage';
import ComposeApprovalPage from './pages/Approvals/ComposeApprovalPage';
import NewApprovalPage from './pages/Approvals/NewApprovalPage';
import ApprovalDetailPage from './pages/Approvals/ApprovalDetailPage';
import ProviderApprovalPortalPage from './pages/Approvals/ProviderApprovalPortalPage';
import DenialAnalysisPage from './pages/DenialAnalysis/DenialAnalysisPage';
import NotesBacklogPage from './pages/Notes/NotesBacklogPage';
import UploadLinksPage from './pages/SecureUpload/UploadLinksPage';
import GenerateLinkPage from './pages/SecureUpload/GenerateLinkPage';
import PublicUploadPage from './pages/SecureUpload/PublicUploadPage';
import PatientNotesPage from './pages/Notes/PatientNotesPage';
import NoteReviewPage from './pages/Notes/NoteReviewPage';
import StyleTemplatePage from './pages/Notes/StyleTemplatePage';
import OverviewPage from './pages/Overview/OverviewPage';
import TeamPage from './pages/Team/TeamPage';
import DesignSystemPage from './pages/DesignSystem';
import BillingPage from './pages/Billing/BillingPage';
import SuccessPage from './pages/Billing/SuccessPage';
import CancelPage from './pages/Billing/CancelPage';
import {
  TermsOfService,
  PrivacyPolicy,
  CookiePolicy,
  SecurityPage,
  DataRetention,
  AccessibilityStatement,
  ComplianceDisclosures
} from './pages/Legal';
import Footer from './components/Footer';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import SubscriptionGate from './components/SubscriptionGate';
import SidebarLayout from './components/SidebarLayout';

function NavBar({ isAuthenticated, onLogout }) {
  const [dataMenuAnchor, setDataMenuAnchor] = useState(null);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);

  return (
    <AppBar position="static" sx={{ bgcolor: 'white', color: 'text.primary', boxShadow: 1 }}>
      <Toolbar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 0, mr: 4, fontWeight: 600, color: 'text.primary' }}>
          <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            AppealBot
          </Link>
        </Typography>
        {isAuthenticated ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
            {/* Primary Action - Appeals */}
            <Button
              variant="contained"
              component={Link}
              to="/appeals"
              startIcon={<ArticleIcon size={16} />}
              sx={{ mr: 2 }}
            >
              Appeals
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/denial-analysis"
              sx={{ color: 'text.secondary' }}
            >
              Analyze Denial
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/notes"
              startIcon={<AssignmentIcon size={16} />}
              sx={{ color: 'text.secondary' }}
            >
              PT Notes
            </Button>

            {/* Secondary - Data Management */}
            <Button
              color="inherit"
              onClick={(e) => setDataMenuAnchor(e.currentTarget)}
              endIcon={<ArrowDownIcon size={16} />}
              sx={{ color: 'text.secondary' }}
            >
              Manage Data
            </Button>
            <Menu
              anchorEl={dataMenuAnchor}
              open={Boolean(dataMenuAnchor)}
              onClose={() => setDataMenuAnchor(null)}
            >
              <MenuItem component={Link} to="/claims" onClick={() => setDataMenuAnchor(null)}>
                Claims
              </MenuItem>
              <MenuItem component={Link} to="/patients" onClick={() => setDataMenuAnchor(null)}>
                Patients
              </MenuItem>
              <MenuItem component={Link} to="/providers" onClick={() => setDataMenuAnchor(null)}>
                Providers
              </MenuItem>
              <MenuItem component={Link} to="/documents" onClick={() => setDataMenuAnchor(null)}>
                Documents
              </MenuItem>
            </Menu>

            {/* Account Menu */}
            <Box sx={{ ml: 'auto' }}>
              <Button
                color="inherit"
                onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
                endIcon={<ArrowDownIcon size={16} />}
                sx={{ color: 'text.secondary' }}
              >
                Account
              </Button>
              <Menu
                anchorEl={accountMenuAnchor}
                open={Boolean(accountMenuAnchor)}
                onClose={() => setAccountMenuAnchor(null)}
              >
                <MenuItem component={Link} to="/profile" onClick={() => setAccountMenuAnchor(null)}>
                  Profile
                </MenuItem>
                <MenuItem component={Link} to="/billing" onClick={() => setAccountMenuAnchor(null)}>
                  Billing
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { setAccountMenuAnchor(null); onLogout(); }}>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        ) : (
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button variant="outlined" component={Link} to="/login">
              Login
            </Button>
            <Button variant="contained" component={Link} to="/signup">
              Sign Up
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}

function AppContent() {
  // Initialize authentication state from localStorage synchronously
  // This prevents redirects on page refresh before useEffect runs
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // eslint-disable-next-line no-restricted-properties -- token presence check, not auth state
    const token = localStorage.getItem('token');
    return !!token;
  });
  const [currentUser, setCurrentUser] = useState(/** @type {any} */ (null));
  const navigate = useNavigate();
  const location = useLocation();
  const isLandingRoute = location.pathname === '/';
  const isAuthRoute = ['/login', '/forgot-password'].includes(location.pathname) || location.pathname.startsWith('/signup') || location.pathname.startsWith('/reset-password') || location.pathname.startsWith('/verify-email');
  const isPublicPortal = location.pathname.startsWith('/u/') || location.pathname === '/portal/approval';
  const isAdmin = currentUser?.is_staff === true;

  useEffect(() => {
    // eslint-disable-next-line no-restricted-properties -- token presence check, not auth state
    const token = localStorage.getItem('token');
    setIsAuthenticated(!!token);
  }, []);

  // Fetch the current user's profile (esp. oa_onboarded) once authenticated.
  // Backed by accounts.UserProfileView at /api/v1/accounts/profile/.
  useEffect(() => {
    if (!isAuthenticated) { setCurrentUser(null); return; }
    let cancelled = false;
    import('./utils/axios').then(({ default: api }) => {
      api.get('/accounts/profile/')
        .then((r) => { if (!cancelled) setCurrentUser(r.data); })
        .catch(() => { if (!cancelled) setCurrentUser(null); });
    });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setIsAuthenticated(false);
    navigate('/login');
  };

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const withSidebar = (element) => (
    <SidebarLayout isAuthenticated={isAuthenticated} onLogout={handleLogout}>
      {element}
    </SidebarLayout>
  );

  // Wrap content with SubscriptionProvider when authenticated
  const content = (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {!isAuthenticated && !isLandingRoute && !isAuthRoute && !isPublicPortal && <NavBar isAuthenticated={isAuthenticated} onLogout={handleLogout} />}

      <Box sx={{ flex: 1 }}>
      <Routes>
        <Route
          path="/"
          element={
            isAuthenticated ?
              <Navigate to="/overview" replace /> :
              <LandingPage />
          }
        />
        <Route
          path="/overview"
          element={
            isAuthenticated ?
              withSidebar(<OverviewPage />) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/login"
          element={
            isAuthenticated ?
              <Navigate to="/overview" replace /> :
              <LoginPage onLogin={handleLogin} />
          }
        />
        <Route
          path="/signup"
          element={
            isAuthenticated ?
              <Navigate to="/profile" replace /> :
              <SignupPage onLogin={handleLogin} />
          }
        />
        {/* Step 2 of signup — Organization. Reached authenticated (register in
            step 1 stores the JWT + fires onLogin); a fresh visit falls back to login. */}
        <Route
          path="/signup/organization"
          element={
            isAuthenticated ?
              <OrganizationPage onLogin={handleLogin} /> :
              <Navigate to="/login" replace />
          }
        />
        {/* Step 3 of signup — Subscription. Reached authenticated after creating
            an org; full-screen AuthShell onboarding pages (no app NavBar/Sidebar). */}
        <Route
          path="/plans"
          element={
            isAuthenticated ?
              <PlansPage /> :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/payment"
          element={
            isAuthenticated ?
              <PaymentPage /> :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/signup/success"
          element={
            isAuthenticated ?
              <SubscriptionSuccessPage /> :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/profile"
          element={
            isAuthenticated ?
              withSidebar(
                <Container sx={{ mt: 4 }}>
                  <Profile />
                </Container>
              ) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/forgot-password"
          element={
            isAuthenticated ?
              <Navigate to="/profile" replace /> :
              <ForgotPasswordPage />
          }
        />
        <Route
          path="/reset-password/:uidb64/:token"
          element={
            isAuthenticated ?
              <Navigate to="/profile" replace /> :
              <ResetPasswordPage />
          }
        />
        <Route
          path="/verify-email/:uidb64/:token"
          element={<EmailVerification />}
        />
        <Route
          path="/patients"
          element={
            isAuthenticated ?
              withSidebar(<PatientsPage />) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/providers"
          element={
            isAuthenticated ?
              withSidebar(<ProvidersPage />) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/documents"
          element={
            isAuthenticated ?
              withSidebar(
                <Container sx={{ mt: 4 }}>
                  <DocumentsPage />
                </Container>
              ) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/claims"
          element={
            isAuthenticated ?
              withSidebar(<ClaimListPage />) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/claims/:claimId/appeal"
          element={
            isAuthenticated ?
              withSidebar(<LineItemAppealPage />) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/claims/:claimId/edit"
          element={
            isAuthenticated ?
              <Navigate to="../" relative="path" replace /> :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/claims/:claimId/track"
          element={
            isAuthenticated ?
              withSidebar(<TrackPage />) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/claims/:claimId/ticket/:ticketId"
          element={
            isAuthenticated ?
              withSidebar(<TicketDetailPage />) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/claims/:claimId"
          element={
            isAuthenticated ?
              withSidebar(<ClaimFlowPage currentUser={currentUser} />) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/appeals"
          element={
            isAuthenticated ?
              withSidebar(
                <SubscriptionGate featureName="appeal generation">
                  <Container sx={{ mt: 4 }}>
                    <AppealsPage />
                  </Container>
                </SubscriptionGate>
              ) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/appeals/new"
          element={
            isAuthenticated ?
              withSidebar(
                <SubscriptionGate featureName="appeal generation">
                  <Container sx={{ mt: 4 }}>
                    <AppealsPage />
                  </Container>
                </SubscriptionGate>
              ) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/appeals/:appealId"
          element={
            isAuthenticated ?
              withSidebar(
                <SubscriptionGate featureName="appeal generation">
                  <Container sx={{ mt: 4 }}>
                    <AppealsPage />
                  </Container>
                </SubscriptionGate>
              ) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/approvals"
          element={
            isAuthenticated ?
              withSidebar(<ApprovalsPage />) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/approvals/new"
          element={
            isAuthenticated ?
              withSidebar(<NewApprovalPage />) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/approvals/compose"
          element={
            isAuthenticated ?
              withSidebar(<ComposeApprovalPage />) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/approvals/:id"
          element={
            isAuthenticated ?
              withSidebar(<ApprovalDetailPage />) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/denial-analysis"
          element={
            isAuthenticated ?
              withSidebar(
                <SubscriptionGate featureName="denial analysis">
                  <DenialAnalysisPage />
                </SubscriptionGate>
              ) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/denial-analysis/:analysisId"
          element={
            isAuthenticated ?
              withSidebar(
                <SubscriptionGate featureName="denial analysis">
                  <DenialAnalysisPage />
                </SubscriptionGate>
              ) :
              <Navigate to="/login" replace />
          }
        />
        {/* PT Notes */}
        <Route
          path="/notes"
          element={
            isAuthenticated ?
              withSidebar(
                <SubscriptionGate featureName="AI note generation">
                  <NotesBacklogPage />
                </SubscriptionGate>
              ) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/notes/patients/:patientId"
          element={
            isAuthenticated ?
              withSidebar(
                <SubscriptionGate featureName="AI note generation">
                  <PatientNotesPage />
                </SubscriptionGate>
              ) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/notes/review/:noteId"
          element={
            isAuthenticated ?
              withSidebar(
                <SubscriptionGate featureName="AI note generation">
                  <NoteReviewPage />
                </SubscriptionGate>
              ) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/notes/style-template"
          element={isAuthenticated ? withSidebar(<StyleTemplatePage />) : <Navigate to="/login" replace />}
        />

        <Route
          path="/team"
          element={
            isAuthenticated ?
              withSidebar(<TeamPage />) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/billing"
          element={
            isAuthenticated ?
              withSidebar(<BillingPage />) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/billing/success"
          element={
            isAuthenticated ?
              withSidebar(<SuccessPage />) :
              <Navigate to="/login" replace />
          }
        />
        <Route
          path="/billing/cancel"
          element={
            isAuthenticated ?
              withSidebar(<CancelPage />) :
              <Navigate to="/login" replace />
          }
        />
        {/* Upload Batches — admin only */}
        <Route
          path="/upload-batches"
          element={
            !isAuthenticated ? <Navigate to="/login" replace />
            : currentUser === null ? null
            : !isAdmin ? <Navigate to="/overview" replace />
            : withSidebar(<UploadLinksPage />)
          }
        />
        <Route
          path="/upload-batches/new"
          element={
            !isAuthenticated ? <Navigate to="/login" replace />
            : currentUser === null ? null
            : !isAdmin ? <Navigate to="/overview" replace />
            : withSidebar(<GenerateLinkPage />)
          }
        />
        {/* Design System — dev only */}
        {process.env.NODE_ENV !== 'production' && (
          <Route path="/design-system" element={<DesignSystemPage />} />
        )}
        {/* Public upload portal — no auth required */}
        <Route path="/u/:token" element={<PublicUploadPage />} />
        {/* Public provider approval portal — token stays in URL fragment */}
        <Route path="/portal/approval" element={<ProviderApprovalPortalPage />} />
        {/* Legal Pages - Public Access */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/data-retention" element={<DataRetention />} />
        <Route path="/accessibility" element={<AccessibilityStatement />} />
        <Route path="/compliance" element={<ComplianceDisclosures />} />
      </Routes>
      </Box>

      {!isAuthenticated && !isLandingRoute && !isAuthRoute && !isPublicPortal && <Footer />}
    </Box>
  );

  // Wrap with SubscriptionProvider when authenticated
  if (isAuthenticated) {
    return (
      <SubscriptionProvider>
        {content}
      </SubscriptionProvider>
    );
  }

  return content;
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
